/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2026. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: main.ts
 * Created by: chan.ying
 * Create Date: 2026-04-27 15:52
 * Version Number: 1.0
 * Version Description:
 *
 * =============================================================================
 */

import {createApp} from 'vue';
import App from './App.vue';
import router from "./router";
import BootstrapVue3 from 'bootstrap-vue-3';
import vClickOutside from "click-outside-vue3";
import Maska from 'maska';
import {i18n, setCurrentLanguage} from "./i18n.js";
import 'sweetalert2/dist/sweetalert2.min.css';
import '@vueform/slider/themes/default.css';
import "./assets/design/tokens/_ztn-tokens.scss";
import "./assets/design/css/ztn-style.css";
import "./assets/design/css/ztn-style-swal.css";
import "./assets/design/css/ztn-style-light.css";
import "./assets/design/css/ztn-style-dark.css";
import "./assets/design/tokens/_ztn-accents.scss";
import "./assets/design/css/ztn-style-accent.css";
import "./assets/design/css/ztn-a11y.css";
import "./assets/design/css/ztn-style-list.css";
import "./assets/design/css/ztn-style-table.scss";
import store from "./state/store";
import "../src/assets/design/app.scss";
import utils from "./utils/index"
import VueCookies from "vue3-cookies";
import {HttpStatus} from "@/commons/https/HttpStatus";
import AppConfig from "@/commons/AppConfig";
import CsrfToken from "@/commons/https/CsrfToken";
// 这里原本还有一行 `import 'bootstrap/dist/css/bootstrap.css'`。已删除，原因：
// app.scss → bootstrap.scss → `~bootstrap/scss/bootstrap`，整套 Bootstrap 已经带着
// _variables.scss 的主题变量编译进来了。再引一份 dist 里的原版，等于把 281KB 未定制的
// Bootstrap 叠在主题之上 —— 同为 :root / 单类选择器、特异性相同、位置更靠后，于是
// 原版反过来盖掉主题。实测：.btn-primary 的 --bs-btn-bg 一直是 Bootstrap 默认的
// #0d6efd，而不是主题色；body 背景走的也是 --bs-body-bg 而不是 --bg-body。
// 也就是说 _variables.scss 里的定制对组件类长期是失效的。
import 'bootstrap-vue-3/dist/bootstrap-vue-3.css';
import skeletonDirective from "@/commons/directive/SkeletonDirective";
import safeHtmlDirective from "@/commons/directive/SafeHtmlDirective";
import {showSessionExpired} from "@/commons/session/SessionExpired";
import SwalUtil from "@/commons/utils/SwalUtil";
import SwalConfig from "@/commons/utils/SwalConfig";
import TimezoneUtil from "@/commons/utils/TimezoneUtil";
import TeamService from "@/services/TeamService";
import OnlineUserManager from "@/security/OnlineUserManager";
import ProfileData from "@/utils/cookie/ProfileData";

// process.env.NODE_TLS_REJECT_UNAUTHORIZED = '0';

// Before the first request goes out: the team-site server refuses every mutating call that does
// not echo its CSRF cookie back in the X-CSRF-Token header, with a 403 the browser then reports
// as a CORS failure. This has to run here, ahead of TeamService.retrieve() below.
CsrfToken.install();

const hideAppLoading = () => {
    const loading = document.getElementById('app-loading');
    if (loading) {
        loading.style.opacity = '0';
        setTimeout(() => {
            loading.style.display = 'none';
        }, 400);
    }
};


// A failure with no HTTP response at all — the service is down, DNS fails, or the
// browser blocked the response because the origin is not in the server's CORS
// allow-list. TeamService maps that to 502 because there is no status to report.
// Signing the user out here would be a lie AND destructive: their session is fine,
// the service simply could not be reached, and logging out loses it for nothing.
const popupServiceUnreachable = (status: number) => {
    SwalUtil.popupNotification(SwalConfig.errorInfo(
        "Error",
        "Cannot reach the team-site service. Check that the service is running and that "
        + "this origin is allowed by its CORS configuration. (status: " + status + ")",
        "Cancel")
    )
};

// Opened on the bare service domain, with no team in the host? Then there is nothing to load:
// every API call would go to a team host built out of the service domain itself. Send the
// visitor to the sign-in page (which links to signup) rather than letting them watch a request to
// a nonexistent host fail. IDAM's logout lands here too: it ignores team-site's return address and
// sends the browser to the bare host, so this is where a signed-out user arrives.
if (AppConfig.Instance.isBareServiceHost) {
    window.location.href = AppConfig.Instance.signinURL();
} else {

TeamService.Instance.retrieve().then((res) => {
    const status = Number(res.status);

    // Only a real authorization answer should send the user back to login. Treating
    // every non-2xx the same way is what made a CORS rejection and a stopped server
    // both surface as "your authorization has expired" — the one message that
    // guarantees the reader looks in the wrong place.
    if (status === HttpStatus.StatusUnauthorized || status === HttpStatus.StatusForbidden
        || status === HttpStatus.StatusFound) {
        showSessionExpired();
        return;
    }
    // 404 with TEAM_NOT_FOUND is already handled inside HttpExecutor: it explains the situation
    // and navigates to signup. Reporting it a second time here would stack a second, wronger
    // dialog ("cannot reach the service") on top of the right one.
    if (status === HttpStatus.StatusNotFound) {
        return;
    }
    if (!res.isHttpStatus2xx()) {
        popupServiceUnreachable(status);
        return;
    }
    // A 2xx with an empty body is just as unusable as an error: without team
    // info the app cannot bootstrap, so treat that as "not authorized".
    if (!res.data) {
        showSessionExpired();
        return;
    }
    {
        OnlineUserManager.Instance.updateTeamInfo(res.data).then((updateRes) => {
            if (!updateRes.isSuccess()) {
                showSessionExpired();
                return;
            }
            const theme = ProfileData.getAppearance();
            let themeStr: string;
            if (theme === ProfileData._ValueThemeOs) {
                themeStr = window.matchMedia("(prefers-color-scheme: dark)").matches ? 'dark' : 'light';
            } else {
                themeStr = theme === ProfileData._ValueThemeDark ? 'dark' : 'light';
            }
            ProfileData.setTheme(theme);
            store.commit('layout/CHANGE_THEME', themeStr);
            store.dispatch('setInitialLanguage', ProfileData.getLanguage());
            setCurrentLanguage(i18n, ProfileData.getLanguage());
            TimezoneUtil.init();
            const app = createApp(App);

            app
                .use(store)
                .use(router)
                .use(require('vue-chartist'))
                .use(BootstrapVue3)
                .use(vClickOutside)
                .use(i18n)
                .use(Maska)
                .use(utils)
                .use(VueCookies)
                .use(skeletonDirective)
                .use(safeHtmlDirective)
                .mount('#app')
            ;
        })
    }
}).catch(() => {
    showSessionExpired();
}).finally(() => {
    hideAppLoading();
})

} // end: not the bare service host
